import React, { ChangeEvent, useContext, FocusEvent, useState } from 'react';

import { observer } from 'mobx-react-lite';
import clsx from 'clsx';

import TextInput from 'components/common/TextInput/TextInput';
import { RootStoreContext } from 'index';
import useForm, { FieldValues } from 'hooks/useForm';
import Button from 'components/common/Button/Button';
import { CompanyInformationFieldName } from 'components/intermediaryApplications/intermediaryApplications.enums';
import { validateEmail } from 'components/auth/authValidation.utils';
import PhoneNumberInput from 'components/common/PhoneNumberInput/PhoneNumberInput';
import InputSelect from 'components/common/InputSelect/InputSelect';
import { EditIcon } from 'components/common/Icons/Icons';
import { PRIMARY_HOME_IMPROVEMENT_CATEGORY_OPTIONS } from 'components/intermediaryApplications/intermediaryApplications.constants';
import { notify } from 'components/common/Toast/toast.helpers';
import { ToastMessage } from 'components/common/Toast/toast.enums';
import styles from 'components/intermediaryApplications/CompanyProfile/CompanyProfile.module.scss';
import { isValidPhone, validateRequiredField } from 'common/utils/validation.utils';
import { hasPermission } from 'components/permissions/permissions.utils';
import { PermissionName } from 'components/permissions/permissions.enum';
import MultipleSelect, { MultipleSelectOption } from 'components/common/MultipleSelect/MultipleSelect';

const CompanyInformation = () => {
  const { intermediaryApplicationsStore, authStore } = useContext(RootStoreContext);
  const { companyProfile, updateCompanyProfile, isLoading } = intermediaryApplicationsStore;
  const [isEditDataActive, setIsEditDataActive] = useState<boolean>(false);
  const canCompanyInformationUpdate = hasPermission(authStore.account!.permissions, [
    PermissionName.CompanyInformationUpdate,
  ]);
  const defaultValues: Partial<Record<CompanyInformationFieldName, string | string[]>> = {
    [CompanyInformationFieldName.Name]: companyProfile?.name || '',
    [CompanyInformationFieldName.ContractorId]: companyProfile?.idNumber || '',
    [CompanyInformationFieldName.PersonalizedLink]: companyProfile?.personalizedLink || '',
    [CompanyInformationFieldName.PrimaryHomeImprovementCategory]: companyProfile?.primaryHomeImprovementCategory || '',
    [CompanyInformationFieldName.OtherHomeImprovementCategory]: companyProfile?.otherHomeImprovementCategory || [],
    [CompanyInformationFieldName.Phone]: companyProfile?.phone || '',
    [CompanyInformationFieldName.Email]: companyProfile?.email || '',
  };

  const { errors, values, setValue, validateField, handleSubmit, resetForm } = useForm<FieldValues>({
    defaultValues: defaultValues,
    handleValidation: (fieldName, value) => {
      switch (fieldName) {
        case CompanyInformationFieldName.PrimaryHomeImprovementCategory: {
          return validateRequiredField(value, fieldName);
        }
        case CompanyInformationFieldName.Phone: {
          return validateRequiredField(value, fieldName) || isValidPhone(value);
        }
        case CompanyInformationFieldName.Email: {
          return validateRequiredField(value, fieldName) || validateEmail(value, fieldName);
        }
      }

      return '';
    },
  });

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = event.target;

    setValue(name, value);
  };

  const handleEditData = () => {
    setIsEditDataActive(true);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>): void => {
    const { name } = event.target;

    validateField(name, values[name]);
  };

  const handleSaveInformation = handleSubmit(async () => {
    try {
      await updateCompanyProfile({
        primaryHomeImprovementCategory: values[CompanyInformationFieldName.PrimaryHomeImprovementCategory] || null,
        otherHomeImprovementCategory: values[CompanyInformationFieldName.OtherHomeImprovementCategory] || null,
        phone: values[CompanyInformationFieldName.Phone] || null,
        email: values[CompanyInformationFieldName.Email] || null,
      });

      notify(ToastMessage.InformationSaved, 'success');
      setIsEditDataActive(false);
    } catch (error: any) {
      notify(error.message || ToastMessage.DefaultError, 'error');
    }
  });

  const handleCancelEditData = () => {
    resetForm();
    setIsEditDataActive(false);
  };

  const handleCopy = async () => {
    const text = values[CompanyInformationFieldName.PersonalizedLink] as string;

    await navigator.clipboard.writeText(text);
    notify('Copied!', 'success');
  };

  return (
    <div className={styles.mainContainer}>
      <div className={styles.inputsContainer}>
        <div className={styles.header}>
          <div className={styles.headerLabel}>Company Information</div>
          {!isEditDataActive && canCompanyInformationUpdate && (
            <Button onClick={handleEditData} className={styles.button} variant="link">
              <EditIcon /> Edit Data
            </Button>
          )}
        </div>
        <div className={styles.formInput}>
          <TextInput
            className={styles.input}
            useRequiredSymbol={isEditDataActive}
            containerClassName={styles.inputContainer}
            labelTitle={CompanyInformationFieldName.Name}
            name={CompanyInformationFieldName.Name}
            value={values[CompanyInformationFieldName.Name]}
            errorMessage={errors[CompanyInformationFieldName.Name]}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={true}
          />
          <TextInput
            className={styles.input}
            useRequiredSymbol={isEditDataActive}
            containerClassName={styles.inputContainer}
            labelTitle={CompanyInformationFieldName.ContractorId}
            name={CompanyInformationFieldName.ContractorId}
            value={values[CompanyInformationFieldName.ContractorId]}
            errorMessage={errors[CompanyInformationFieldName.ContractorId]}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={true}
          />
        </div>
        <div className={styles.formInput}>
          <InputSelect
            items={PRIMARY_HOME_IMPROVEMENT_CATEGORY_OPTIONS}
            disabled={!isEditDataActive}
            currentValue={values[CompanyInformationFieldName.PrimaryHomeImprovementCategory]}
            onChange={(value) => setValue(CompanyInformationFieldName.PrimaryHomeImprovementCategory, value)}
            label={CompanyInformationFieldName.PrimaryHomeImprovementCategory}
            errorMessage={errors[CompanyInformationFieldName.PrimaryHomeImprovementCategory]}
            useRequiredSymbol={isEditDataActive}
          />
          <MultipleSelect
            id="home-categories"
            selectOptions={PRIMARY_HOME_IMPROVEMENT_CATEGORY_OPTIONS.filter(
              (option) => option.label !== values[CompanyInformationFieldName.PrimaryHomeImprovementCategory],
            )}
            disabled={!isEditDataActive}
            useSelectAll
            selectAllLabel="All categories"
            errorMessage={errors[CompanyInformationFieldName.OtherHomeImprovementCategory]}
            inputLabel={CompanyInformationFieldName.OtherHomeImprovementCategory}
            initialValues={
              values[CompanyInformationFieldName.OtherHomeImprovementCategory].map((category: string) =>
                PRIMARY_HOME_IMPROVEMENT_CATEGORY_OPTIONS.find((option) => option.value === category),
              ) as MultipleSelectOption[]
            }
            onChange={(value) =>
              setValue(
                CompanyInformationFieldName.OtherHomeImprovementCategory,
                value.map((option) => option.value),
              )
            }
          />
        </div>
        <div className={styles.formInput}>
          <PhoneNumberInput
            className={styles.input}
            useRequiredSymbol={isEditDataActive}
            containerClassName={styles.inputContainer}
            labelTitle={CompanyInformationFieldName.Phone}
            onChange={(value: any) => setValue(CompanyInformationFieldName.Phone, value)}
            data-label={CompanyInformationFieldName.Phone}
            value={values[CompanyInformationFieldName.Phone]}
            required={true}
            disabled={!isEditDataActive}
            errorMessage={errors[CompanyInformationFieldName.Phone]}
          />
          <TextInput
            className={styles.input}
            useRequiredSymbol={isEditDataActive}
            containerClassName={styles.inputContainer}
            labelTitle={CompanyInformationFieldName.Email}
            name={CompanyInformationFieldName.Email}
            value={values[CompanyInformationFieldName.Email]}
            errorMessage={errors[CompanyInformationFieldName.Email]}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={!isEditDataActive}
          />
        </div>
        <div className={styles.formInputHalf}>
          <TextInput
            className={clsx(styles.input, styles.orangeText)}
            useRequiredSymbol={isEditDataActive}
            containerClassName={styles.inputContainer}
            labelTitle={CompanyInformationFieldName.PersonalizedLink}
            name={CompanyInformationFieldName.PersonalizedLink}
            value={values[CompanyInformationFieldName.PersonalizedLink]}
            errorMessage={errors[CompanyInformationFieldName.PersonalizedLink]}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={true}
            onCopyInput={handleCopy}
            useCopyInput={true}
          />
        </div>
        <div className={styles.formInput}>
          {isEditDataActive && (
            <div className={styles.editButtons}>
              <Button isLoading={isLoading} onClick={handleSaveInformation} className={styles.saveButton}>
                Save Changes
              </Button>
              <Button className={styles.cancelButton} onClick={handleCancelEditData}>
                Cancel
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default observer(CompanyInformation);
